import { Fragment, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	Divider,
	Grid2,
	TextField,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import {
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import type { RollsetListItem } from "../models/RollshopOperations";
import {
	formatDiameter,
	getRollsetPositions,
	type OperatorOption,
	type RollsetPosition,
} from "../models/Rollshop";
import { useActiveOperators, useCutRolls } from "../hooks/useRollshop";
import {
	COMPACT_DIALOG_SX,
	COMPACT_OPTION_SX,
	EDITABLE_FIELD_SX,
} from "./compactDialog";
import { ReadOnlyField } from "./ReadOnlyField";

interface RollInput {
	newDiameter: string;
	timeToCut: string;
}

interface Props {
	open: boolean;
	rollset: RollsetListItem | null;
	onClose: () => void;
	onTurned: () => void;
}

const numberInput = {
	textAlign: "right",
	fontVariantNumeric: "tabular-nums",
} as const;

// "Top Roll" / "Bottom Roll" on common stands, "Roll 1" to "Roll 5" on the others.
const rollLabel = (p: RollsetPosition) =>
	/^\d+$/.test(p.label) ? `Roll ${p.label}` : `${p.label} Roll`;

export const TurnRollsModal = ({ open, rollset, onClose, onTurned }: Props) => {
	const positions = useMemo(
		() => (rollset ? getRollsetPositions(rollset) : []),
		[rollset],
	);
	const [inputs, setInputs] = useState<Record<string, RollInput>>({});
	const [operator1, setOperator1] = useState<OperatorOption | null>(null);
	const [operator2, setOperator2] = useState<OperatorOption | null>(null);
	const [comments, setComments] = useState("");
	const [serverError, setServerError] = useState("");

	const { data: operators = [] } = useActiveOperators();
	const { cutRolls, isCutting } = useCutRolls({
		onSuccess: onTurned,
		onError: (error) => setServerError(error.message),
	});

	// Legacy: the new diameter starts at the current one, so only the turned rolls change.
	// Filled when the modal opens, not on every refetch, so typing is never overwritten.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset only on open
	useEffect(() => {
		if (!open) return;
		setInputs(
			Object.fromEntries(
				positions.map((p) => [
					p.position,
					{
						newDiameter: p.diameter == null ? "" : String(p.diameter),
						timeToCut: "",
					},
				]),
			),
		);
		setOperator1(null);
		setOperator2(null);
		setComments("");
		setServerError("");
	}, [open]);

	const errors = useMemo(() => {
		const result: Record<string, string> = {};
		for (const p of positions) {
			const raw = inputs[p.position]?.newDiameter ?? "";
			const value = Number(raw);
			if (raw.trim() === "" || Number.isNaN(value) || value <= 0)
				result[p.position] = "Required";
			else if (p.diameter != null && value > p.diameter)
				result[p.position] = `Max ${formatDiameter(p.diameter)}`;
		}
		return result;
	}, [inputs, positions]);

	const setInput = (position: string, field: keyof RollInput, value: string) =>
		setInputs((prev) => ({
			...prev,
			[position]: { ...prev[position], [field]: value },
		}));

	const handleSubmit = () => {
		if (!rollset || Object.keys(errors).length > 0) return;
		setServerError("");
		cutRolls({
			rollsetId: rollset.Id,
			rolls: positions.map((p) => {
				const input = inputs[p.position];
				return {
					position: p.position,
					newDiameter: Number(input.newDiameter),
					timeToCut:
						input.timeToCut.trim() === "" ? null : Number(input.timeToCut),
				};
			}),
			operator1Id: operator1?.Id ?? null,
			operator2Id: operator2?.Id ?? null,
			comments: comments.trim() === "" ? null : comments.trim(),
		});
	};

	const operatorField = (
		label: string,
		value: OperatorOption | null,
		onChange: (value: OperatorOption | null) => void,
	) => (
		<Autocomplete
			options={operators}
			value={value}
			autoHighlight
			size="small"
			fullWidth
			getOptionLabel={(option) => option.Name}
			isOptionEqualToValue={(option, current) => option.Id === current.Id}
			onChange={(_event, option) => onChange(option)}
			renderOption={(props, option) => {
				const { key, ...rest } = props as typeof props & { key: string };
				return (
					<li key={option.Id} {...rest} style={COMPACT_OPTION_SX}>
						{option.Name}
					</li>
				);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					sx={EDITABLE_FIELD_SX}
					label={label}
					slotProps={{ inputLabel: { shrink: true } }}
				/>
			)}
		/>
	);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DetailHeader
				title={`Turn ${rollset?.Name ?? ""}`}
				isSaving={isCutting}
				onClose={onClose}
			/>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				<DialogContent sx={{ p: 2, ...COMPACT_DIALOG_SX }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
						<DetailSection title="Rolls" icon={<TrackChangesIcon />}>
							{/* One line per roll: what it is (read only), then what the turn leaves. */}
							<Grid2 container spacing={1.5} sx={{ pt: 0.5 }}>
								{positions.map((p) => (
									<Fragment key={p.position}>
										{/* A line above each roll: under the section title, then between rolls. */}
										<Grid2 size={12}>
											<Divider />
										</Grid2>
										<Grid2 size={3}>
											<ReadOnlyField label={rollLabel(p)} value={p.rollName} />
										</Grid2>
										<Grid2 size={3}>
											<ReadOnlyField
												label="Previous Diameter"
												value={formatDiameter(p.diameter)}
												numeric
											/>
										</Grid2>
										<Grid2 size={3}>
											<TextField
												sx={EDITABLE_FIELD_SX}
												label="New Diameter"
												type="number"
												size="small"
												fullWidth
												value={inputs[p.position]?.newDiameter ?? ""}
												onChange={(e) =>
													setInput(p.position, "newDiameter", e.target.value)
												}
												error={!!errors[p.position]}
												helperText={errors[p.position]}
												slotProps={{
													inputLabel: { shrink: true },
													htmlInput: {
														step: 0.001,
														min: 0,
														style: numberInput,
													},
													formHelperText: { sx: { mx: 0 } },
												}}
											/>
										</Grid2>
										<Grid2 size={3}>
											<TextField
												sx={EDITABLE_FIELD_SX}
												label="Turn Time (min)"
												type="number"
												size="small"
												fullWidth
												value={inputs[p.position]?.timeToCut ?? ""}
												onChange={(e) =>
													setInput(p.position, "timeToCut", e.target.value)
												}
												slotProps={{
													inputLabel: { shrink: true },
													htmlInput: { step: 1, min: 0, style: numberInput },
												}}
											/>
										</Grid2>
									</Fragment>
								))}
							</Grid2>
						</DetailSection>
						<DetailSection title="Operators" icon={<EngineeringOutlinedIcon />}>
							<Grid2 container spacing={1}>
								<Grid2 size={{ xs: 12, md: 6 }}>
									{operatorField("Operator 1", operator1, setOperator1)}
								</Grid2>
								<Grid2 size={{ xs: 12, md: 6 }}>
									{operatorField("Operator 2", operator2, setOperator2)}
								</Grid2>
							</Grid2>
						</DetailSection>
						<DetailSection title="Comments" icon={<ChatBubbleOutlineIcon />}>
							<TextField
								sx={EDITABLE_FIELD_SX}
								label="Comments"
								value={comments}
								onChange={(e) => setComments(e.target.value)}
								fullWidth
								multiline
								minRows={2}
								maxRows={6}
								size="small"
								slotProps={{
									inputLabel: { shrink: true },
									htmlInput: { maxLength: 400 },
								}}
							/>
						</DetailSection>
						{serverError && <Alert severity="error">{serverError}</Alert>}
					</Box>
				</DialogContent>
				<Divider />
				<DialogActions sx={{ px: 2, py: 1.5 }}>
					<FooterModalButtons
						isSubmitLoading={isCutting}
						disabled={Object.keys(errors).length > 0}
						onHide={onClose}
						buttonLabel="Turn"
					/>
				</DialogActions>
			</form>
		</Dialog>
	);
};
